"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader, ArrowRight, Instagram } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
export default function RequestAQuote() {
  return <section data-section-id="1707" id="secure-your-flight" className="bg-gradient-to-b from-[#0A0A0A] to-[#050505] py-24 relative overflow-hidden dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          
          <div className="lg:col-span-5 flex flex-col justify-center">
            <h2 className="font-sans text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tighter">
              Elevate Your Journey
            </h2>
            <p className="text-zinc-400 text-lg mb-12 max-w-md font-light">
              Experience the pinnacle of private aviation. Contact our advisors to orchestrate your next mission with absolute discretion and precision.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-none">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-sans text-sm uppercase tracking-widest font-bold text-white mb-1">Direct Line</h3>
                  <p className="text-zinc-400">+1 (203) 751-1897</p>
                  <p className="text-xs text-zinc-500 mt-1">24/7 Global Operations</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-none">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-sans text-sm uppercase tracking-widest font-bold text-white mb-1">Concierge</h3>
                  <p className="text-zinc-400">jwallace@starboardaviationgroup.com<br /></p>
                  <p className="text-xs text-zinc-500 mt-1">Encrypted Communication</p>
                </div>
              </div>

              <Link to="https://www.instagram.com/starboardaviationgroup/?__d=1%252F" newTab>
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-none">
                    <Instagram className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans text-sm uppercase tracking-widest font-bold text-white mb-1">Instagram</h3>
                    <p className="text-zinc-400">@starboardaviationgroup</p>
                    <p className="text-xs text-zinc-500 mt-1">Follow us</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 bg-[#121212] p-8 md:p-12 shadow-2xl border border-zinc-800/50">
            <div className="mb-8">
              <h3 className="font-sans text-2xl font-bold text-white uppercase tracking-tight">Secure Your Flight</h3>
              <p className="text-zinc-500 mt-2 text-sm">Provide your details below. Our flight operations team will respond within 60 minutes.</p>
            </div>

            <FormProvider formKey="secure_flight_form_home" formId="secure_flight_form_home" sectionName="request-quote-section" defaultValues={{
            full_name: "",
            company_name: "",
            email: "",
            phone: "",
            product_type: "",
            quantity: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: "Full Name is required"
            },
            company_name: {
              required: "Organization is required"
            },
            email: {
              required: "Email is required",
              pattern: "email"
            },
            phone: {
              required: "Phone Number is required",
              pattern: "phone"
            },
            product_type: {
              required: "Please select a service"
            }
          }} submitText="Request a Quote" successMessage="Your flight request has been transmitted. An advisor will contact you shortly.">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const inputClasses = "bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary h-12 text-base text-white placeholder:text-zinc-700 transition-colors";
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 bg-zinc-900/50">
        <div className="w-16 h-16 bg-primary/10 flex items-center justify-center rounded-full mb-4">
          <ArrowRight className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-white uppercase tracking-widest">Request Transmitted</h3>
        <p className="text-zinc-400 max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="ALEXANDER VANCE" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Organization / Family Office</FormLabel>
            <FormControl>
              <Input placeholder="VANCE GLOBAL HOLDINGS" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Priority Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="vance@global.com" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Direct Phone</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="product_type" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Service Required</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={cn(inputClasses, "w-full")}>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                <SelectItem value="Consulting">Aviation Consulting</SelectItem>
                <SelectItem value="Trip Support">Trip Support (Part 91/135)</SelectItem>
                <SelectItem value="Charter">Charter Brokerage</SelectItem>
                <SelectItem value="Sourcing">Aircraft Sourcing</SelectItem>
                <SelectItem value="Sales">Sales & Acquisitions</SelectItem>
                <SelectItem value="Management">Jet Management</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="quantity" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Passengers <span className="text-zinc-600 font-normal ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Input type="number" placeholder="e.g., 8" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
          <FormLabel className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Trip Details <span className="text-zinc-600 font-normal ml-1">(Optional)</span></FormLabel>
          <FormControl>
            <Textarea {...field} placeholder="Route, dates, specific aircraft preferences, or catering requirements." className={cn(inputClasses, "h-32 py-3 resize-none")} />
          </FormControl>
          <FormMessage />
        </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none text-sm font-bold tracking-widest uppercase shadow-xl transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Transmitting...
          </> : "Request a Quote"}
      </Button>
    </Form>;
}